import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  Input,
  LoadingState,
  toast,
} from '@sitewise/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { fetchMe } from '../../../api/auth.js';
import {
  confirmPhoneVerification,
  fetchNotificationPreferences,
  removePhone,
  startPhoneVerification,
  updateNotificationPreferences,
  updateQuietHours,
  type NotificationPreference,
  type QuietHours,
} from '../../../api/me.js';
import { ApiError } from '../../../lib/api-client.js';
import { useSyncStore } from '../../../stores/sync-store.js';

const CATEGORY_LABELS: Record<string, string> = {
  work_order: 'Work orders',
  compliance: 'Compliance',
  inspection: 'Inspections',
  violation: 'Violations',
  vendor: 'Vendor activity',
  reminder: 'Reminders',
  system: 'System announcements',
};

export function NotificationsSettingsPage() {
  const queryClient = useQueryClient();
  const track = useSyncStore((s) => s.track);

  const prefsQuery = useQuery({
    queryKey: ['notification-preferences'],
    queryFn: fetchNotificationPreferences,
  });
  const meQuery = useQuery({ queryKey: ['me'], queryFn: fetchMe });

  const [draft, setDraft] = useState<NotificationPreference[]>([]);
  const [quietDraft, setQuietDraft] = useState<QuietHours | null>(null);

  useEffect(() => {
    if (prefsQuery.data) {
      setDraft(prefsQuery.data.preferences);
      setQuietDraft(prefsQuery.data.quietHours);
    }
  }, [prefsQuery.data]);

  const savePrefs = useMutation({
    mutationFn: () => track(updateNotificationPreferences({ preferences: draft })),
    onSuccess: () => {
      toast.success('Notification preferences saved');
      queryClient.invalidateQueries({ queryKey: ['notification-preferences'] });
    },
  });

  const saveQuiet = useMutation({
    mutationFn: () => track(updateQuietHours(quietDraft!)),
    onSuccess: () => {
      toast.success('Quiet hours saved');
      queryClient.invalidateQueries({ queryKey: ['notification-preferences'] });
    },
  });

  if (prefsQuery.isPending) return <LoadingState rows={3} />;
  if (prefsQuery.isError) {
    return (
      <Alert variant="danger">
        <AlertDescription>Could not load preferences.</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="space-y-4 pt-6">
          <header>
            <h2 className="text-lg font-semibold">Channels &amp; digest</h2>
            <p className="text-sm text-fg-secondary">
              Choose which channels deliver alerts for each category, and how often to bundle emails.
            </p>
          </header>

          <div className="overflow-x-auto rounded border border-border">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-surface-muted/50 text-xs uppercase text-fg-muted">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">Category</th>
                  <th className="px-3 py-2 text-center font-semibold">Email</th>
                  <th className="px-3 py-2 text-center font-semibold">SMS</th>
                  <th className="px-3 py-2 text-center font-semibold">In-app</th>
                  <th className="px-3 py-2 text-left font-semibold">Digest</th>
                </tr>
              </thead>
              <tbody>
                {draft.map((pref, idx) => (
                  <tr key={pref.category} className="border-b border-border last:border-b-0">
                    <td className="px-3 py-2.5 font-medium">
                      {CATEGORY_LABELS[pref.category] ?? pref.category}
                      {pref.emailUnsubscribed ? (
                        <span className="ml-2 text-xs italic text-fg-muted">
                          (unsubscribed via email link)
                        </span>
                      ) : null}
                    </td>
                    <ChannelCell
                      checked={pref.channelEmail}
                      onChange={(v) =>
                        setDraft((curr) =>
                          curr.map((p, i) => (i === idx ? { ...p, channelEmail: v } : p)),
                        )
                      }
                    />
                    <ChannelCell
                      checked={pref.channelSms}
                      onChange={(v) =>
                        setDraft((curr) =>
                          curr.map((p, i) => (i === idx ? { ...p, channelSms: v } : p)),
                        )
                      }
                    />
                    <ChannelCell
                      checked={pref.channelInApp}
                      onChange={(v) =>
                        setDraft((curr) =>
                          curr.map((p, i) => (i === idx ? { ...p, channelInApp: v } : p)),
                        )
                      }
                    />
                    <td className="px-3 py-2.5">
                      <select
                        className="w-full rounded border border-border bg-surface px-2 py-1 text-sm"
                        value={pref.digestFrequency}
                        onChange={(e) =>
                          setDraft((curr) =>
                            curr.map((p, i) =>
                              i === idx
                                ? {
                                    ...p,
                                    digestFrequency: e.target.value as NotificationPreference['digestFrequency'],
                                  }
                                : p,
                            ),
                          )
                        }
                      >
                        <option value="realtime">Real-time</option>
                        <option value="daily">Daily digest</option>
                        <option value="weekly">Weekly digest</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {savePrefs.error ? (
            <Alert variant="danger">
              <AlertDescription>
                {savePrefs.error instanceof ApiError
                  ? savePrefs.error.message
                  : 'Could not save preferences.'}
              </AlertDescription>
            </Alert>
          ) : null}

          <div className="flex justify-end">
            <Button onClick={() => savePrefs.mutate()} disabled={savePrefs.isPending}>
              {savePrefs.isPending ? 'Saving…' : 'Save channels'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-4 pt-6">
          <header>
            <h2 className="text-lg font-semibold">Quiet hours</h2>
            <p className="text-sm text-fg-secondary">
              Pause email and SMS during the window below. In-app notifications still arrive.
            </p>
          </header>

          {quietDraft ? (
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={quietDraft.enabled}
                  onChange={(e) =>
                    setQuietDraft((d) => (d ? { ...d, enabled: e.target.checked } : d))
                  }
                />
                Enable quiet hours
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={quietDraft.urgentOverrides}
                  onChange={(e) =>
                    setQuietDraft((d) => (d ? { ...d, urgentOverrides: e.target.checked } : d))
                  }
                />
                Let urgent items through
              </label>
              <label className="text-sm">
                <span className="mb-1 block text-fg-secondary">Start</span>
                <Input
                  type="time"
                  value={quietDraft.startLocalTime}
                  onChange={(e) =>
                    setQuietDraft((d) => (d ? { ...d, startLocalTime: e.target.value } : d))
                  }
                />
              </label>
              <label className="text-sm">
                <span className="mb-1 block text-fg-secondary">End</span>
                <Input
                  type="time"
                  value={quietDraft.endLocalTime}
                  onChange={(e) =>
                    setQuietDraft((d) => (d ? { ...d, endLocalTime: e.target.value } : d))
                  }
                />
              </label>
              <label className="text-sm sm:col-span-2">
                <span className="mb-1 block text-fg-secondary">Timezone</span>
                <Input
                  value={quietDraft.timezone}
                  onChange={(e) =>
                    setQuietDraft((d) => (d ? { ...d, timezone: e.target.value } : d))
                  }
                  placeholder="America/New_York"
                />
              </label>
            </div>
          ) : null}

          <div className="flex justify-end">
            <Button
              onClick={() => saveQuiet.mutate()}
              disabled={!quietDraft || saveQuiet.isPending}
            >
              {saveQuiet.isPending ? 'Saving…' : 'Save quiet hours'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <PhoneVerifyCard
        currentPhone={meQuery.data?.user?.phone ?? null}
        verified={Boolean(meQuery.data?.user?.phoneVerifiedAt)}
      />
    </div>
  );
}

function ChannelCell({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <td className="px-3 py-2.5 text-center">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 cursor-pointer rounded border-border-strong accent-[var(--color-primary)]"
      />
    </td>
  );
}

function PhoneVerifyCard({
  currentPhone,
  verified,
}: {
  currentPhone: string | null;
  verified: boolean;
}) {
  const queryClient = useQueryClient();
  const [phone, setPhone] = useState(currentPhone ?? '');
  const [stage, setStage] = useState<'idle' | 'awaiting-code'>(verified ? 'idle' : 'idle');
  const [code, setCode] = useState('');

  const startMutation = useMutation({
    mutationFn: () => startPhoneVerification(phone),
    onSuccess: () => {
      setStage('awaiting-code');
      toast.success('Verification code sent');
    },
  });

  const confirmMutation = useMutation({
    mutationFn: () => confirmPhoneVerification(code),
    onSuccess: () => {
      setStage('idle');
      setCode('');
      toast.success('Phone verified');
      queryClient.invalidateQueries({ queryKey: ['me'] });
    },
  });

  const removeMutation = useMutation({
    mutationFn: removePhone,
    onSuccess: () => {
      setPhone('');
      setStage('idle');
      toast.success('Phone removed');
      queryClient.invalidateQueries({ queryKey: ['me'] });
    },
  });

  return (
    <Card>
      <CardContent className="space-y-4 pt-6">
        <header>
          <h2 className="text-lg font-semibold">SMS number</h2>
          <p className="text-sm text-fg-secondary">
            Verify a phone number to receive urgent SMS alerts. We never send SMS without verification.
          </p>
        </header>

        {verified && currentPhone ? (
          <div className="flex items-center justify-between rounded border border-border bg-surface-muted/40 p-3">
            <div>
              <div className="text-sm font-medium">{currentPhone}</div>
              <div className="text-xs text-success">Verified</div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => removeMutation.mutate()}
              disabled={removeMutation.isPending}
            >
              Remove
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <label className="block text-sm">
              <span className="mb-1 block text-fg-secondary">Phone (E.164)</span>
              <Input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+15551234567"
              />
            </label>
            {stage === 'idle' ? (
              <Button
                onClick={() => startMutation.mutate()}
                disabled={!phone || startMutation.isPending}
              >
                {startMutation.isPending ? 'Sending code…' : 'Send code'}
              </Button>
            ) : (
              <>
                <label className="block text-sm">
                  <span className="mb-1 block text-fg-secondary">Verification code</span>
                  <Input
                    inputMode="numeric"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="123456"
                  />
                </label>
                <div className="flex gap-2">
                  <Button
                    onClick={() => confirmMutation.mutate()}
                    disabled={code.length !== 6 || confirmMutation.isPending}
                  >
                    {confirmMutation.isPending ? 'Verifying…' : 'Verify'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => startMutation.mutate()}
                    disabled={startMutation.isPending}
                  >
                    Resend code
                  </Button>
                </div>
              </>
            )}
          </div>
        )}

        {(startMutation.error || confirmMutation.error || removeMutation.error) && (
          <Alert variant="danger">
            <AlertDescription>
              {(startMutation.error as ApiError)?.message ??
                (confirmMutation.error as ApiError)?.message ??
                (removeMutation.error as ApiError)?.message ??
                'Phone update failed.'}
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
